import { PendingAttachmentPicker } from '@components/Attachments'
import { countLabel } from '@/utils/plural'
import { DateTimePicker } from '@components/DateTimePicker'
import LocationAutocomplete, { PlaceSuggestion } from '@components/LocationAutocomplete'
import Modal from '@components/Modal'
import { useAuth } from '@context/AuthContext'
import { CreateAccommodationPayload } from '@hooks/useCreateAccommodation'
import { dateToInput, inputToUtcString } from '@lib/datetime'
import { GetUserType } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import {
    AddLinkButton,
    Button,
    ButtonGroup,
    CompactFormLabel,
    DateInputGroup,
    DateInputRow,
    FormDescription,
    FormGroup,
    FormInput,
    FormLabel,
    FormTextArea,
    GuestCheckbox,
    GuestError,
    GuestName,
    GuestRow,
    GuestSummary,
    GuestsList,
    LinkInput,
    LinkRow,
    LinksSection,
    LocationSecondaryText,
    RemoveLinkButton,
} from './AddAccommodationModal.styles'
import { backgroundOrange } from '@/theme/colors'
import { Avatar } from '@components/Avatar'

const MAX_LINKS = 3

export type AddAccommodationSubmitData = CreateAccommodationPayload & { pendingFiles?: File[] }

interface AddAccommodationModalProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (data: AddAccommodationSubmitData) => void
    members: GetUserType[]
    isSubmitting?: boolean
}

const AddAccommodationModal: React.FC<AddAccommodationModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    members,
    isSubmitting = false,
}) => {
    const { user } = useAuth()

    const [name, setName] = useState('')
    const [locationPrimaryText, setLocationPrimaryText] = useState('')
    const [selectedPlace, setSelectedPlace] = useState<PlaceSuggestion | null>(null)
    const [checkInDate, setCheckInDate] = useState('')
    const [checkOutDate, setCheckOutDate] = useState('')
    const [notes, setNotes] = useState('')
    const [links, setLinks] = useState<string[]>([])
    const [selectedGuests, setSelectedGuests] = useState<Set<number>>(new Set())
    const [pendingFiles, setPendingFiles] = useState<File[]>([])

    useEffect(() => {
        if (isOpen) {
            setName('')
            setLocationPrimaryText('')
            setSelectedPlace(null)
            const now = new Date()
            setCheckInDate(dateToInput(now))
            const tomorrow = new Date(now)
            tomorrow.setDate(tomorrow.getDate() + 1)
            setCheckOutDate(dateToInput(tomorrow))
            setNotes('')
            setLinks([])
            setSelectedGuests(new Set(members.map(m => m.id)))
            setPendingFiles([])
        }
    }, [isOpen, members])

    const handleLocationPrimaryTextChange = (value: string) => {
        setLocationPrimaryText(value)
        setSelectedPlace(null)
    }

    const handleLocationSelect = (place: PlaceSuggestion) => {
        setSelectedPlace(place)
        if (name.trim() === '') {
            setName((place.primaryText ?? place.fullText).substring(0, 75).trim())
        }
    }

    const toggleGuest = (userId: number) => {
        setSelectedGuests(prev => {
            const next = new Set(prev)
            if (next.has(userId)) {
                next.delete(userId)
            } else {
                next.add(userId)
            }
            return next
        })
    }

    const addLink = () => {
        if (links.length < MAX_LINKS) {
            setLinks(prev => [...prev, ''])
        }
    }

    const updateLink = (index: number, value: string) => {
        setLinks(prev => prev.map((l, i) => (i === index ? value : l)))
    }

    const removeLink = (index: number) => {
        setLinks(prev => prev.filter((_, i) => i !== index))
    }

    const canSubmit =
        name.trim().length > 0 &&
        checkInDate.length > 0 &&
        checkOutDate.length > 0 &&
        selectedGuests.size > 0 &&
        new Date(checkInDate) < new Date(checkOutDate) &&
        !isSubmitting

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!canSubmit) return

        const filteredLinks = links.filter(l => l.trim().length > 0)

        const location = selectedPlace
            ? {
                fullText: selectedPlace.fullText,
                primaryText: selectedPlace.primaryText,
                secondaryText: selectedPlace.secondaryText || undefined,
                googlePlaceId: selectedPlace.placeId,
                googleTypes: selectedPlace.types,
            }
            : locationPrimaryText.trim()
                ? {
                    fullText: locationPrimaryText.trim(),
                    primaryText: locationPrimaryText.trim(),
                    googleTypes: [],
                }
                : undefined

        const payload: AddAccommodationSubmitData = {
            name: name.trim(),
            checkInDate: inputToUtcString(checkInDate),
            checkOutDate: inputToUtcString(checkOutDate),
            guestUserIds: Array.from(selectedGuests),
            notes: notes.trim() || null,
            links: filteredLinks.length > 0 ? filteredLinks : undefined,
            location,
            pendingFiles: pendingFiles.length > 0 ? pendingFiles : undefined,
        }

        onSubmit(payload)
    }


    const getMemberName = (member: GetUserType) => {
        const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ')
        return member.id === user?.id ? `${fullName} (You)` : fullName
    }

    const sortedMembers = [...members].sort((a, b) => {
        if (a.id === user?.id) return -1
        if (b.id === user?.id) return 1
        return 0
    })

    const dateError = checkInDate && checkOutDate && new Date(checkInDate) >= new Date(checkOutDate)
        ? 'Check-out must be after check-in'
        : null

    const formId = 'add-accommodation-form'

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Add Accommodation"
            size="large"
            backgroundColor={backgroundOrange}
            closeOnBackdropClick={false}
            footer={
                <ButtonGroup>
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" form={formId} variant="primary" disabled={!canSubmit}>
                        {isSubmitting ? 'Adding...' : 'Add Accommodation'}
                    </Button>
                </ButtonGroup>
            }
        >
            <form id={formId} onSubmit={handleSubmit}>
                <FormGroup>
                    <FormLabel htmlFor="accommodation-location">Location (Optional)</FormLabel>
                    <LocationAutocomplete
                        id="accommodation-location"
                        value={locationPrimaryText}
                        onChange={handleLocationPrimaryTextChange}
                        onPlaceSelect={handleLocationSelect}
                        placeholder="Search for a location..."
                        autoFocus
                    />
                    {selectedPlace?.secondaryText && (
                        <LocationSecondaryText>
                            {selectedPlace.secondaryText}
                        </LocationSecondaryText>
                    )}
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="accommodation-name">Name</FormLabel>
                    <FormInput
                        id="accommodation-name"
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="e.g. Marriott Downtown"
                        maxLength={75}
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel>Dates</FormLabel>
                    <DateInputRow>
                        <DateInputGroup>
                            <CompactFormLabel htmlFor="checkin-date">Check-in</CompactFormLabel>
                            <DateTimePicker
                                id="checkin-date"
                                value={checkInDate}
                                onChange={next => {
                                    setCheckInDate(next)
                                    if (checkOutDate && next && next >= checkOutDate) {
                                        setCheckOutDate('')
                                    }
                                }}
                                placeholder="Select check-in"
                            />
                        </DateInputGroup>
                        <DateInputGroup>
                            <CompactFormLabel htmlFor="checkout-date">Check-out</CompactFormLabel>
                            <DateTimePicker
                                id="checkout-date"
                                value={checkOutDate}
                                onChange={next => {
                                    setCheckOutDate(next)
                                    if (checkInDate && next && next <= checkInDate) {
                                        setCheckInDate('')
                                    }
                                }}
                                placeholder="Select check-out"
                            />
                        </DateInputGroup>
                    </DateInputRow>
                    {dateError && (
                        <GuestSummary>
                            <GuestError>{dateError}</GuestError>
                        </GuestSummary>
                    )}
                </FormGroup>

                <FormGroup>
                    <FormLabel>Guests</FormLabel>
                    <FormDescription>Select who is staying at this accommodation</FormDescription>
                    <GuestsList>
                        {sortedMembers.map(member => {
                            const isSelected = selectedGuests.has(member.id)
                            return (
                                <GuestRow
                                    key={member.id}
                                    $isSelected={isSelected}
                                    onClick={() => toggleGuest(member.id)}
                                >
                                    <GuestCheckbox $checked={isSelected} />
                                    <Avatar user={member} size="md" />
                                    <GuestName>{getMemberName(member)}</GuestName>
                                </GuestRow>
                            )
                        })}
                    </GuestsList>
                    <GuestSummary>
                        {selectedGuests.size === 0 ? (
                            <GuestError>Select at least one guest</GuestError>
                        ) : (
                            `${countLabel(selectedGuests.size, 'guest')} selected`
                        )}
                    </GuestSummary>
                </FormGroup>

                <FormGroup>
                    <FormLabel>Links</FormLabel>
                    <FormDescription>Add booking confirmations or relevant URLs (max {MAX_LINKS})</FormDescription>
                    <LinksSection>
                        {links.map((link, index) => (
                            <LinkRow key={index}>
                                <LinkInput
                                    type="text"
                                    value={link}
                                    onChange={e => updateLink(index, e.target.value)}
                                    placeholder="e.g. https://booking.com/..."
                                />
                                <RemoveLinkButton type="button" onClick={() => removeLink(index)}>
                                    ✕
                                </RemoveLinkButton>
                            </LinkRow>
                        ))}
                        <AddLinkButton
                            type="button"
                            onClick={addLink}
                            disabled={links.length >= MAX_LINKS}
                        >
                            + Add Link
                        </AddLinkButton>
                    </LinksSection>
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="accommodation-notes">Notes</FormLabel>
                    <FormDescription>Add any relevant notes about the accommodation</FormDescription>
                    <FormTextArea
                        id="accommodation-notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="e.g. Check-in code is 1234, parking is in garage B..."
                    />
                </FormGroup>

                <PendingAttachmentPicker files={pendingFiles} onChange={setPendingFiles} />
            </form>
        </Modal>
    )
}

export default AddAccommodationModal
